/**
 * React Query bindings for a workspace's API keys (`services/api-key.service.ts`):
 * Settings > API keys, and the agent web-widget picker that reads the public
 * ones. Each write says how it went in a toast; a refusal's toast is the
 * server's sentence.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { type ApiKey, type ApiKeyType, createApiKey, listApiKeys, revokeApiKey } from "@/services/api-key.service";

export type WorkspaceApiKey = ApiKey;

export const useWorkspaceApiKeys = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ["workspace-api-keys", workspaceId],
    queryFn: () => listApiKeys(workspaceId!),
    enabled: !!workspaceId,
  });
};

/** Resolves to the new key, whose `api_key` is the whole key — the only time it is shown. */
export const useCreateApiKey = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      name,
      keyType = "private",
      expiresAt,
    }: {
      workspaceId: string;
      name: string;
      keyType?: ApiKeyType;
      expiresAt?: string;
    }) => createApiKey(workspaceId, { name, key_type: keyType, expires_at: expiresAt }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-api-keys", variables.workspaceId] });
      toast({
        title: "API key created",
        description: "Your API key has been generated. Save it now - it won't be shown again.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create API key",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useRevokeApiKey = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keyId, workspaceId }: { keyId: string; workspaceId: string }) => revokeApiKey(workspaceId, keyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-api-keys", variables.workspaceId] });
      toast({
        title: "API key revoked",
        description: "The API key has been revoked successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke API key",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
